import {
  activeItemsByDomain, capacityStatus, completionCounts, readCapacitySnapshot,
  type CapacityStatus,
} from './capacityCore';
import { useCapacityBlockStore } from '../stores/capacityBlockStore';
import type { ActionTypeId } from '../constants/achievementBadges';
import type { InboxDestination } from '../types/inbox';

/**
 * THE CREATE GUARD — the one place capacity stops you adding something.
 *
 * ⚠️⚠️ THIS IS A DELIBERATE REVERSAL of the "capacity never blocks" rule that
 * `CapacityIndicator.tsx` and `constants/achievementBadges.ts` carried until
 * 2026-08-03 (owner's call, same day). It is NOT a re-run of the lockout this
 * codebase removed. That one was unwinnable by construction — it gated projects
 * on level 10 and routines on level 15 through `calculateCapacity`, which
 * returns **0** below the unlock level, so `held >= max` was `0 >= 0` and every
 * non-developer account was blocked at ZERO held with no reachable remedy. The
 * rules below exist so that cannot happen again:
 *
 *  1. **THE CAP IS NEVER ZERO.** Every allowance comes from `capacityFor`, whose
 *     floor is the type's `baseCapacity` — and every `baseCapacity` is >= 1,
 *     asserted in `achievementBadges.test.ts`. Nothing consults a level.
 *  2. **NO OPINION MEANS ALLOW.** `capacityStatus` returns `null` for an unknown
 *     domain or a per-parent type with no parent; the guard treats that as
 *     permission. A cap that fires when it could not compute is the bug.
 *  3. **THE BLOCK ALWAYS HAS AN EXIT.** Every block opens a modal offering at
 *     least the Inbox, and `capacityEscapes.ts` only offers Complete/Archive
 *     where the action provably reduces the very count that blocked.
 *  4. **NO DEVELOPER BYPASS.** `developer_mode` deliberately does NOT loosen
 *     this. The old lockout was invisible to the owner precisely because that
 *     flag returned `max: 999`, so the only account that could have caught it
 *     never saw it. Owner and user hit identical behaviour here.
 *
 * ⚠️ GRANDFATHERING IS FREE, NOT A SPECIAL CASE. Accounts already over a cap
 * keep every record they hold — nothing here deletes, archives or completes
 * anything on its own. `used >= max` is simply already true for them, so they
 * are stopped from ADDING and otherwise untouched. That is exactly the agreed
 * `cap = max(declared, held)`.
 */

/**
 * The block rule, alone, so a test can state it without a store.
 *
 * `adding` is how many records the caller is about to create — 1 for a person
 * pressing Create, N for an AI batch applying a whole plan at once.
 *
 * ⚠️ At `adding = 1` this is exactly the original `used >= max`, which is why
 * every single-item caller was left untouched.
 */
export function isAtCapacity(status: CapacityStatus | null, adding = 1): boolean {
  // ⚠️ `used + adding > max`, so the LAST allowed record still fits. `over` on
  // `CapacityStatus` is a DISPLAY flag meaning "already past the line" and is a
  // different question — do not reuse it here.
  if (!status) return false;   // rule 2: no opinion means allow
  return status.used + adding > status.max;
}

/**
 * Read capacity for a create attempt, live off the stores.
 *
 * ⚠️⚠️ `readCapacitySnapshot()` on every call, deliberately — never a value
 * captured at render. Two creates dispatched inside one React batch share a
 * render closure, so a memoised reader would let the second sail past a cap the
 * first had just filled.
 */
export function readCapacityFor(
  type: ActionTypeId, domainId: string | undefined, parentId?: string,
): CapacityStatus | null {
  // ⚠️ An uncategorised record has no domain, so it has no allowance to spend.
  // Allowing it is the only honest answer — the alternative is blocking on a
  // limit that was never computed.
  if (!domainId) return null;
  const snap = readCapacitySnapshot();
  return capacityStatus(
    snap, activeItemsByDomain(snap), completionCounts(snap), type, domainId, parentId);
}

/** `true` when the create attempt would exceed the domain's allowance. */
export function isCreateBlocked(
  type: ActionTypeId, domainId: string | undefined, parentId?: string, adding = 1,
): boolean {
  return isAtCapacity(readCapacityFor(type, domainId, parentId), adding);
}

export interface CapacityRequest {
  type: ActionTypeId;
  /** Where the new record would land. `undefined` = uncategorised = allowed. */
  domainId: string | undefined;
  /** Quest id for a goal, ability id for a skill — required for the per-parent types. */
  parentId?: string;
  /**
   * How many of this type are about to be created. Defaults to 1.
   *
   * ⚠️ Only a BATCH needs this. An AI plan can create eight tasks in one press,
   * and eight checks of "is there room for one more" all pass at 2-of-5 while
   * the batch as a whole does not fit.
   */
  adding?: number;
  /** Carried into the Inbox escape so a blocked thought is not lost. */
  draftTitle?: string;
  draftDescription?: string;
}

/**
 * ⭐⭐ THE ONE CALL EVERY CREATE PATH MAKES BEFORE IT CREATES.
 *
 * ```ts
 * if (!ensureCapacity({ type: 'quests', domainId: category, draftTitle: title })) return;
 * addQuest({ … });
 * ```
 *
 * Returns `true` to proceed. `false` means the create was blocked and the modal
 * is already open — the caller must return WITHOUT closing its own form. Leaving
 * the form open matters: the user may free a slot in the modal and press Create
 * again, and a form that closed itself would have thrown their input away.
 *
 * ⭐ A PLAIN FUNCTION, NOT A HOOK, so the same guard serves components,
 * `services/` and `utils/` alike. A hook would have forced a second, drifting
 * copy for every non-React create path.
 *
 * ⚠️⚠️ IT SITS AT THE CREATE PATHS, NOT INSIDE THE STORE ACTIONS, and that is a
 * deliberate choice rather than a shortcut. `addQuest`/`addHabit`/… are also
 * called by paths where a silent refusal would be a data-loss bug: a marketplace
 * import runs AFTER the wallet is debited, and prayer-habit seeding creates
 * records the user never asked for by hand. Blocking is a conversation with a
 * person who just pressed a button; it belongs where that button is.
 * `capacityGuard.test.ts` enumerates every create call site in the codebase and
 * fails on one that is neither guarded nor exempted with a stated reason — so
 * "nineteen of twenty were wired" cannot pass tsc, lint and the suite the way it
 * otherwise would.
 */
export function ensureCapacity(req: CapacityRequest): boolean {
  // ⚠️ No domain = no allowance to spend. See `readCapacityFor`.
  if (!req.domainId) return true;
  const adding = req.adding ?? 1;
  // ⚠️ A batch that creates nothing of this type must not be checked at all —
  // `adding: 0` on a full domain would otherwise block a plan that adds none.
  if (adding <= 0) return true;
  if (!isCreateBlocked(req.type, req.domainId, req.parentId, adding)) return true;

  useCapacityBlockStore.getState().openCapacityBlock({
    type: req.type,
    domainId: req.domainId,
    parentId: req.parentId,
    adding,
    draftTitle: req.draftTitle?.trim() || undefined,
    draftDescription: req.draftDescription?.trim() || undefined,
  });
  return false;
}

/**
 * ALL-OR-NOTHING capacity check for a batch that creates several types at once.
 *
 * ⚠️⚠️ IT MUST RUN BEFORE ANY WRITE — AND BEFORE ANY CHARGE. Both AI apply
 * paths debit Coin first and create second, so a refusal discovered halfway
 * through would take the user's Coin and leave them a partial record set. This
 * reads every request against the live stores and commits to nothing.
 *
 * ⭐ Fails on the FIRST blocked request rather than collecting them: the modal
 * explains one allowance and offers remedies for it, and a user who frees space
 * there can simply press Apply again.
 */
export function ensureCapacityForBatch(reqs: CapacityRequest[]): boolean {
  for (const req of aggregateCapacityRequests(reqs)) {
    if (!ensureCapacity(req)) return false;
  }
  return true;
}

/**
 * ⚠️⚠️ SUM THE BATCH PER ALLOWANCE BEFORE CHECKING IT. Each request carries
 * `adding: 1` by default, and every check reads the SAME live holdings — so ten
 * one-task requests with one slot free all passed and ten tasks landed. The
 * allowance is per (type, domain, parent), so that is the key; the first
 * request's draft text is kept for the modal.
 */
export function aggregateCapacityRequests(reqs: CapacityRequest[]): CapacityRequest[] {
  const byKey = new Map<string, CapacityRequest>();
  for (const req of reqs) {
    const key = `${req.type}|${req.domainId ?? ''}|${req.parentId ?? ''}`;
    const seen = byKey.get(key);
    const adding = req.adding ?? 1;
    if (seen) seen.adding = (seen.adding ?? 1) + adding;
    else byKey.set(key, { ...req, adding });
  }
  return [...byKey.values()];
}

/**
 * The action type an inbox item should be pre-set to when a block sends it
 * there.
 *
 * ⚠️ The two vocabularies are NOT the same list — `InboxDestination` also
 * carries `requirement`, `journal`, `reference`, `someday` and `none`, which no
 * action type maps to. Written out rather than derived by de-pluralising, so a
 * new action type is a compile error here instead of a silently missing route.
 */
/**
 * The reverse: which action type an inbox destination would spend, or `null`
 * for the destinations that create nothing an allowance measures.
 *
 * ⚠️⚠️ THE INBOX EXIT MUST BE GUARDED TOO, or the whole cap is bypassable in
 * two clicks — and the bypass would be the guard's OWN escape hatch, since
 * "put it in the Inbox instead" is what a block offers. An item waits in the
 * inbox until there is room; it does not smuggle itself past the limit.
 *
 * ⚠️ `requirement` is deliberately null: a requirement lives inside a Skill and
 * is not one of the ten counted types. Nor are journal / reference / someday /
 * none, which by design create nothing outside the inbox.
 */
export const ACTION_TYPE_FOR_DESTINATION: Record<InboxDestination, ActionTypeId | null> = {
  task: 'tasks',
  habit: 'habits',
  project: 'projects',
  event: 'events',
  quest: 'quests',
  goal: 'goals',
  ability: 'abilities',
  skill: 'skills',
  program: 'programs',
  routine: 'routines',
  requirement: null,
  journal: null,
  reference: null,
  someday: null,
  none: null,
};

export const INBOX_DESTINATION_FOR: Record<ActionTypeId, InboxDestination> = {
  tasks: 'task',
  habits: 'habit',
  projects: 'project',
  events: 'event',
  quests: 'quest',
  goals: 'goal',
  abilities: 'ability',
  skills: 'skill',
  programs: 'program',
  routines: 'routine',
};
